import React from 'react';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

// Styled component for the amenities container
// Keeping the original absolute positioning
const AmenitiesOverlay = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  position: 'absolute',
  top: '10px',
  right: '10px',
  zIndex: 10
}));

// Styled component for each amenity icon with negative margin for overlap
const AmenityIcon = styled('div')(({ theme, index }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  marginLeft: index === 0 ? 0 : '-12px', // Increased overlap to 16px for tighter stacking
  zIndex: 100 - index, // Higher z-index values with greater difference for clearer layering
  '&:hover': {
    zIndex: 101, // When hovered, bring to front
    transform: 'translateY(-2px)',
    transition: 'transform 0.2s'
  }
}));

// Styled component for the "+X more" indicator
// Styled component for the "+X more" indicator
const MoreAmenities = styled('div')(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: theme.palette.grey[200],
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
  marginLeft: '-8px', // Match the overlap of other icons
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  zIndex: 1, // Place it on top of all icons
  '&:hover': {
    backgroundColor: theme.palette.grey[300],
    transform: 'translateY(-2px)',
    transition: 'transform 0.2s, background-color 0.2s'
  }
}));

const StackedAmenities = ({ space, getIconComponent, theme, maxDisplay = 3 }) => {
  if (!space?.venue_services || space.venue_services.length === 0) {
    return null;
  }

  return (
    <AmenitiesOverlay>
      {space.venue_services.slice(0, maxDisplay).map((amenity, index) => {
        const IconComponent = getIconComponent(amenity.icon);
        return (
          <Tooltip
            key={amenity.slug || `amenity-${index}`}
            title={amenity.description}>
            <AmenityIcon index={index}>
              <IconComponent
                sx={{
                  fontSize: '1.2rem',
                  color: theme.palette.text.primary
                }}
              />
            </AmenityIcon>
          </Tooltip>
        );
      })}

      {space.venue_services.length > maxDisplay && (
        <Tooltip title="More amenities">
          <MoreAmenities>
            +{space.venue_services.length - maxDisplay}
          </MoreAmenities>
        </Tooltip>
      )}
    </AmenitiesOverlay>
  );
};

export default StackedAmenities;